import { ChevronDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { ContratoVinculable } from "@/lib/api";

/**
 * Combo buscable de contratos reales para la columna "Contrato vinculado" de
 * /escenarios. Escribe para filtrar por folio, contraparte o modalidad; el
 * catálogo ya viene acotado al producto y al lado (compra/venta) de la
 * operación, así que solo se ofrecen contratos que de verdad la respaldan.
 */
export function ComboContrato({
  contratos,
  value,
  folio,
  onChange,
  disabled,
  placeholder = "Buscar contrato…",
}: {
  /** Catálogo ya filtrado por producto + lado de la operación. */
  contratos: ContratoVinculable[];
  /** id_contrato seleccionado (null = sin contrato). */
  value: number | null | undefined;
  /** Folio guardado con la operación (se muestra aunque el contrato ya no esté en el catálogo). */
  folio?: string | null;
  onChange: (c: ContratoVinculable | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const raiz = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const sel = contratos.find((c) => c.id_contrato === value) ?? null;
  const etiquetaSel = sel ? `${sel.folio} · ${sel.contraparte}` : folio ?? "";

  const norm = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtrados = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return contratos;
    const palabras = t.split(/\s+/);
    return contratos.filter((c) => {
      const hay = norm(`${c.folio} ${c.contraparte} ${c.modalidad ?? ""} ${c.productos.join(" ")}`);
      return palabras.every((p) => hay.includes(p));
    });
  }, [contratos, q]);

  useEffect(() => setIdx(0), [q, abierto]);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!abierto) return;
    const h = (ev: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(ev.target as Node)) {
        setAbierto(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [abierto]);

  const elegir = (c: ContratoVinculable | null) => {
    onChange(c);
    setAbierto(false);
    setQ("");
  };

  return (
    <div ref={raiz} className="relative w-56">
      <div
        className={`flex items-center border rounded text-sm bg-white ${
          disabled ? "bg-slate-50 text-slate-400" : ""
        } ${!sel && folio ? "border-amber-300" : ""}`}
      >
        <input
          ref={input}
          type="text"
          disabled={disabled}
          value={abierto ? q : etiquetaSel}
          placeholder={placeholder}
          title={
            !sel && folio
              ? `Contrato "${folio}" ya no aparece entre los que respaldan esta operación`
              : etiquetaSel || placeholder
          }
          onFocus={() => setAbierto(true)}
          onChange={(e) => {
            setQ(e.target.value);
            if (!abierto) setAbierto(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setAbierto(true);
              setIdx((i) => Math.min(i + 1, filtrados.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (abierto && filtrados[idx]) elegir(filtrados[idx]);
            } else if (e.key === "Escape") {
              setAbierto(false);
              setQ("");
              input.current?.blur();
            }
          }}
          className="flex-1 min-w-0 px-2 py-1 bg-transparent outline-none truncate"
        />
        {(sel || folio) && !disabled ? (
          <button
            type="button"
            title="Quitar contrato vinculado"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => elegir(null)}
            className="px-1 text-slate-400 hover:text-red-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDown className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
        )}
      </div>
      {abierto && !disabled && (
        <ul className="absolute z-30 mt-1 left-0 w-80 max-h-64 overflow-auto bg-white border border-slate-200 rounded shadow-lg text-sm">
          {filtrados.length === 0 && (
            <li className="px-3 py-2 text-slate-400">
              {contratos.length === 0
                ? "No hay contratos disponibles de este producto y tipo (los ya vinculados a otra operación no se repiten)."
                : "Sin coincidencias."}
            </li>
          )}
          {filtrados.map((c, i) => (
            <li
              key={c.id_contrato}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setIdx(i)}
              onClick={() => elegir(c)}
              className={`px-3 py-1.5 cursor-pointer ${
                i === idx ? "bg-emerald-50" : ""
              } ${c.id_contrato === value ? "font-medium" : ""}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-slate-700">{c.folio}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  {c.estado}
                </span>
              </div>
              <div className="text-xs text-slate-600 truncate">{c.contraparte}</div>
              <div className="text-[11px] text-slate-400 truncate">
                {c.productos.join(" · ")}
                {c.fecha_inicio ? ` · ${c.fecha_inicio} → ${c.fecha_fin ?? "…"}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
